import { StatusCode } from "api/protocols";
import { GetReceipt, EmailReceipt, PrintERangePins } from "api/rpc/2024-04/facilityAdmin/order/order";
import { IGiftCard, PrintGiftCard } from "api/rpc/2022-09/facilityAdmin/payment/giftCard";
import Sheet from "components/sheet/Sheet";
import React, { SetStateAction, useEffect, useRef, useState } from "react";
import { showError, showSuccess } from "redux/actions/ui";
import { IOrder } from "redux/reducers/models/order";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import FormLayout from "components/form/FormLayout";
import { ButtonNew as Button } from "components/buttonNew";
import Spin from "components/spin/spin";
import ReactDOM from "react-dom";
import { GetCartWaiver } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { useHistory } from "react-router";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { printDesktopReceipt } from "helpers/Helpers";
import { useTranslation } from "react-i18next";
import { ICartLineItem, IDiscountLines } from "redux/reducers/models/cart";
import DataTable from "components/dataTable/DataTable";
import { LocaleCurrency } from "helpers/Locale";
import "./orderCompletePopup.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { GetTicketStubPdf } from "api/rpc/2024-04/facilityAdmin/ticket/stub";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { ILoyaltyProgramTransaction } from "redux/reducers/models/loyalty";
import { GetGiftCard } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";
import { GetAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts";

type THouseAccountLookup = {
  id?: number;
  title: string;
  balance: number;
  currency: string;
};

interface IProps {
  /**Boolean that determines if the action bar is displayed */
  open: boolean;
  /**Function that sets the state of the open boolean */
  setOpen: (open: boolean) => void;
  /**Current active order */
  order: Record<string, any> | IOrder | null;
  tee_time_id?: number;
  /** Booking being checked in; print counts are tracked per booking. */
  teeTimeBookingId?: number;
  /** Max auto-printed cart waivers for this booking (1 per every 2 players) */
  cartWaiverLimit?: number;
  /** Persisted count of cart waivers already printed for this booking */
  waiversPrinted?: number;
  /** Claim/persist a cart waiver print for the booking (auto-print claims before printing) */
  onCartWaiverPrinted?: (bookingId: number) => void | Promise<void>;
  ticketStubIds?: Array<number>;
  returnable?: boolean;
  /** Replaces the tee sheet return button label when returnable is set. */
  returnLabel?: string;
  /** Called instead of navigating to the tee sheet. */
  onReturn?: () => void;
  disableDuringLoad?: boolean;
  loading?: boolean;
  transactionComplete?: boolean;
  /**Set to true to load the gift cards when the order is completed */
  teeSheetCheckIn?: boolean;
}

interface IEmailState {
  modalVisible: boolean;
  email: string;
}

export default function OrderCompletePopup(props: IProps) {
  const {
    open,
    setOpen,
    order,
    tee_time_id,
    teeTimeBookingId,
    cartWaiverLimit,
    waiversPrinted,
    onCartWaiverPrinted,
    returnable,
    returnLabel,
    onReturn,
    disableDuringLoad,
    loading,
    transactionComplete,
    teeSheetCheckIn,
    ticketStubIds,
  } = props;
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const { t } = useTranslation();
  const history = useHistory();
  const autoPrintedCartWaiverRef = useRef(false);
  const [orderGiftCards, setOrderGiftCards] = useState<IGiftCard[]>([]);
  const [giftCardPaymentLookupCards, setGiftCardPaymentLookupCards] = useState<IGiftCard[]>([]);
  const [giftCardsLoading, setGiftCardsLoading] = useState<boolean>(false);
  const [houseAccounts, setHouseAccounts] = useState<THouseAccountLookup[]>([]);
  const [emailState, setEmailState] = useState<IEmailState>({
    modalVisible: false,
    email: "",
  });

  const [loyaltyPointsDiscounts, setLoyaltyPointsDiscounts] = useState<Array<IDiscountLines>>([]);
  const [loyaltyCredit, setLoyaltyCredit] = useState<Array<ILoyaltyProgramTransaction>>([]);

  function isHouseAccountTransaction(transaction: any) {
    const method = transaction.payment_method.toLowerCase();
    const type = transaction.payment_type.toLowerCase().replace(/_/g, " ");
    return method === "account" || type === "account";
  }

  function isGiftCardTransaction(transaction: any){
    const type = transaction?.payment_type?.toLowerCase()?.replace(/_/g, " ");
    return type === "gift card";
  };

  useEffect(() => {
    if (!open) {
      autoPrintedCartWaiverRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && order) {
      const giftCardInOrder = order?.line_items?.some(
        (item: Record<string, any>) => item?.product_type === "Gift Card",
      );
      const powerCartInOrder = order?.line_items?.some(
        (item: Record<string, any>) => item?.product_type === "Power Cart",
      );
      const register = JSON.parse(localStorage.getItem("register"));

      const giftCardPaymentUsed = order?.transactions?.some(isGiftCardTransaction);
      const houseAccountPaymentUsed = order?.transactions?.some((transaction: any) =>
        isHouseAccountTransaction(transaction),
      );

      if (giftCardInOrder || giftCardPaymentUsed) {
        void handleSetGiftCardIds();
      }
      if (houseAccountPaymentUsed) {
        void handleSetHouseAccounts();
      }
      if (powerCartInOrder && register.print_by_default) {
        const remaining = Math.max((cartWaiverLimit ?? 0) - (waiversPrinted ?? 0), 0);
        if (remaining > 0 && !autoPrintedCartWaiverRef.current) {
          autoPrintedCartWaiverRef.current = true;
          // Claim the print slot before printing so the next checkout sees the updated count
          // even if the PUT / tee-time refresh is still in flight.
          void (async () => {
            if (teeTimeBookingId != null) {
              await onCartWaiverPrinted?.(teeTimeBookingId);
            }
            await printCartWaiver(false);
          })();
        }
      }

      const loyaltyDiscounts = order?.discount_lines?.filter((discount: IDiscountLines) => discount?.loyalty_points_reward_id);
      const loyaltyCreditTransactions = order?.loyalty_program_transactions?.filter((transaction: ILoyaltyProgramTransaction) => transaction?.kind === "credit");
      if (loyaltyDiscounts?.length > 0) {
        setLoyaltyPointsDiscounts(loyaltyDiscounts as SetStateAction<IDiscountLines[]>);
      }

      if (loyaltyCreditTransactions?.length > 0) {
        setLoyaltyCredit(loyaltyCreditTransactions as SetStateAction<ILoyaltyProgramTransaction[]>);
      }

    } else {
      ReactDOM.unstable_batchedUpdates(() => {
        setOrderGiftCards([]);
        setGiftCardPaymentLookupCards([]);
        setHouseAccounts([]);
        setLoyaltyPointsDiscounts([]);
        setLoyaltyCredit([]);
      });
    }
  }, [open, order]);

  useEffect(() => {
    if (teeSheetCheckIn && order && (order?.financial_status === "paid" || order?.financial_status === "partially_paid")) {
      const giftCardInOrder = order?.line_items?.some((item: Record<string, any>) => item?.product_type === "Gift Card");
      const giftCardPaymentUsed = order?.transactions?.some(
        (transaction: Record<string, any>) => {
          const type = transaction?.payment_type?.toLowerCase()?.replace(/_/g, " ");
          return type === "gift card";
        },
      );
      const houseAccountPaymentUsed = order?.transactions?.some((transaction: Record<string, unknown>) =>
        isHouseAccountTransaction(transaction),
      );
      if (giftCardInOrder || giftCardPaymentUsed) {
        void handleSetGiftCardIds();
      }
      if (houseAccountPaymentUsed) {
        void handleSetHouseAccounts();
      }
    }
  }, [order]);

  async function handleSetHouseAccounts() {
    const transactions = (order?.transactions ?? []) as Array<Record<string, unknown>>;
    const accountTransactions = transactions.filter(isHouseAccountTransaction);
    if (accountTransactions.length === 0) {
      setHouseAccounts([]);
      return;
    }

    const accountIds = new Set<number>();
    for (const transaction of accountTransactions) {
      if (typeof transaction.account_id === "number") {
        accountIds.add(transaction.account_id);
      }
    }

    const matchedAccounts: THouseAccountLookup[] = [];
    const matchedIds = new Set<number>();
    for (const id of accountIds) {
      const accountRes = await GetAccount({ id }, false);
      if (accountRes.status === StatusCode.OK && Array.isArray(accountRes.data) && accountRes.data[0]) {
        const account = accountRes.data[0] as THouseAccountLookup;
        if (typeof account.id === "number" && !matchedIds.has(account.id)) {
          matchedIds.add(account.id);
          matchedAccounts.push(account);
        }
      }
    }

    setHouseAccounts(matchedAccounts);
  }

  async function handleSetGiftCardIds() {
    setGiftCardsLoading(true);
    const orderGiftCardRes = await GetGiftCard({ order_id: order?.id, last_reload_order_id: order?.id }, false);
    const orderList: IGiftCard[] =
      orderGiftCardRes.status === StatusCode.OK && orderGiftCardRes.data ? orderGiftCardRes.data : [];
    const orderCardIdSet = new Set(orderList.map(gc => gc.id));

    const paymentLast4s = new Set<string>();
    const transactions = (order?.transactions ?? []) as Array<Record<string, unknown>>;
    for (const transaction of transactions) {
      if (!isGiftCardTransaction(transaction)) {
        continue;
      }
      const last4 = transaction.payment_last_4;
      if (typeof last4 === "string" && last4.length > 0) {
        paymentLast4s.add(last4);
      }
    }

    let paymentLookupList: IGiftCard[] = [];
    if (order?.customer_id && paymentLast4s.size > 0) {
      const customerGiftCardRes = await GetGiftCard({ customer_id: order.customer_id }, false);
      if (customerGiftCardRes.status === StatusCode.OK && customerGiftCardRes.data) {
        paymentLookupList = customerGiftCardRes.data.filter(
          gc => paymentLast4s.has(gc.last4) && !orderCardIdSet.has(gc.id),
        );
      }
    }

    const resolvedLast4s = new Set([
      ...orderList.map(gc => gc.last4),
      ...paymentLookupList.map(gc => gc.last4),
    ]);
    const unresolvedLast4s = [...paymentLast4s].filter(last4 => !resolvedLast4s.has(last4));
    if (unresolvedLast4s.length > 0) {
      for (const last4 of unresolvedLast4s) {
        const searchRes = await GetGiftCard({ search: last4 }, false);
        if (searchRes.status === StatusCode.OK && searchRes.data) {
          paymentLookupList.push(
            ...searchRes.data.filter(gc => gc.last4 === last4 && !orderCardIdSet.has(gc.id)),
          );
        }
      }
    }


    ReactDOM.unstable_batchedUpdates(() => {
      setGiftCardsLoading(false);
      setOrderGiftCards(orderList);
      setGiftCardPaymentLookupCards(paymentLookupList);
    });
  }

  function closeActionBar() {
    setOpen(false);
  }

  async function printReceipt() {
    if (order) {
      const receiptRes = await GetReceipt({ id: order?.id, tee_time_id, ticket_stub_ids: ticketStubIds && ticketStubIds?.length > 0 ? ticketStubIds : undefined }, true);
      if (receiptRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error printing receipt")));
        return;
      }
      printDesktopReceipt(facilityStore.facility?.id, receiptRes.data.toString() as string);
    }
  }

  async function printCartWaiver(trackPrint = true) {
    const waiverRes = await GetCartWaiver(
      {
        tee_time_id: tee_time_id,
        customer_id: order?.customer?.id,
        order_id: order?.id,
      },
      true,
    );

    if (waiverRes.status === StatusCode.OK) {
      printDesktopReceipt(facilityStore.facility?.id, waiverRes.data.toString() as string);
      if (trackPrint && teeTimeBookingId != null) {
        await onCartWaiverPrinted?.(teeTimeBookingId);
      }
    } else {
      dispatch(showError(t("Cart waiver has not been created")));
    }
  }

  async function emailReceipt() {
    const emailReceiptRes = await EmailReceipt({ order_id: order?.id, customer_email: emailState?.email }, true);
    if (emailReceiptRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending email receipt")));
      return;
    }
    setEmailState(prevState => ({ ...prevState, modalVisible: false, email: "" }));
    dispatch(showSuccess(t("Email receipt sent successfully")));
  }

  async function printGiftCardReceipts() {
    if (order) {
      for (const giftCard of orderGiftCards) {
        const receiptRes = await PrintGiftCard({ id: giftCard?.id, last4: giftCard?.last4 }, true);
        if (receiptRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error printing gift card receipt")));
          return;
        }
        printDesktopReceipt(facilityStore.facility?.id, receiptRes.data.toString() as string);
      }
    }
  }
  async function printERangePins() {
    const receiptRes = await PrintERangePins({ order_id: order?.id }, true);
    if (receiptRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error printing gift card receipt")));
      return;
    }
    printDesktopReceipt(facilityStore.facility?.id, receiptRes.data.toString() as string);
  }

  async function handleTicketDownload(format?: "zebra") {
    if (!order) {
      return;
    }

    const params = {
      order_id: order?.id,
      format: format,
    };

    const downloadReceiptRes = await GetTicketStubPdf(params, true);

    if (downloadReceiptRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error downloading tickets")));
      return;
    }

    window.open(downloadReceiptRes.data as URL, "_blank");
  }

  function handleEmailInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setEmailState(prevState => ({ ...prevState, [id]: value }));
  }

  function returnToTeeSheet() {
    if (onReturn) {
      onReturn();
      return;
    }
    history.push("/admin/teesheet");
  }

  return (
    <>
      <Popup
        type={loading === true ? "info" : "success"}
        open={open}
        title={
          order
            ? loading === true
              ? `${t("Order")} ${order?.name as string}`
              : `${t("Order")} ${order?.name as string} ${t("complete")}`
            : t("Order is being created")
        }
        description={loading === true ? t("Order is processing") : ""}
        closable={loading === true ? false : true}
        onOk={closeActionBar}
        okDisabled={loading === true ? true : false}
        okText={t("Close")}
        backDropCancel={false}
        loading={loading}
        noFixedWidth={loyaltyPointsDiscounts?.length > 0 || order?.loyalty_program_transactions?.length > 0}
      >
        {order?.transactions?.filter((transaction: any) => {
          const type = transaction?.payment_type?.toLowerCase()?.replace(/_/g, " ");
          return type === "gift card";
        })?.map((transaction: any, index: number) => {
          const giftCard =
            orderGiftCards.find(gc => gc.last4 === transaction.payment_last_4) ??
            giftCardPaymentLookupCards.find(gc => gc.last4 === transaction.payment_last_4);
          if (!giftCard) {
            return null;
          }

          const multipleCards = order?.transactions?.filter((t: any) => {
            const type = t?.payment_type?.toLowerCase()?.replace(/_/g, " ");
            return type === "gift card";
          })?.length > 1;

          return (
            <p key={index} className="ui-description-popup">
              {multipleCards ? `${giftCard.code}: ` : t("Remaining gift card balance: ")}
              <LocaleCurrency 
              amount={giftCard.balance} 
              currency={giftCard.currency} 
              style={{ fontWeight: 600, marginLeft: "5px" }} />
            </p>
          );
        })}

        {order?.transactions?.filter((transaction: any) => isHouseAccountTransaction(transaction))
          ?.map((transaction: any, index: number) => {
            const houseAccount = houseAccounts.find(account => account.id === transaction.account_id);

            if (!houseAccount) {
              return null;
            }

            const multipleAccounts =
              order?.transactions?.filter((t: any) => isHouseAccountTransaction(t))?.length > 1;

            return (
              <p key={`house-account-${index}`} className="ui-description-popup">
                {multipleAccounts ? `${houseAccount.title}: ` : t("House account balance: ")}
                <LocaleCurrency
                  amount={houseAccount.balance}
                  currency={houseAccount.currency}
                  style={{ fontWeight: 600, marginLeft: "5px" }}
                />
              </p>
            );
          })}


        {/*---------- LOYALTY POINTS ------------ */}

        {/* REDEEMING POINTS */}
        {loyaltyPointsDiscounts?.length > 0 ? (
          <div className="order-complete-table">
            <br />
            <DataTable
              columns={[{ label: t("Qty") }, { label: t("Item") }, { label: t("Price") }, {
                label: t("Pts"), content: <div className="order-complete-table-header">{t("Pts")} <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id={"points-details"}>{t("Points earned or redeemed")} (+/-)</Tooltip>}
                >
                  <FontAwesomeIcon icon={faCircleQuestion} />
                </OverlayTrigger></div>,
              }]}
            >
              {loyaltyPointsDiscounts?.map((discount, index) => {
                const lineItem = order?.line_items?.find((lineItem: ICartLineItem) => discount?.order_line_item_id === lineItem?.id);
                return (
                  <tr key={index}>
                    <td>{lineItem?.quantity}</td>
                    <td><p>{lineItem?.product_title ? lineItem?.product_title : discount?.title}</p> {lineItem?.product_title !== lineItem?.variant_title ? lineItem?.variant_title : null}</td>
                    <td style={{ color: "#B42318" }}>-<LocaleCurrency amount={discount?.amount} currency={facilityStore?.facility?.default_currency_code ?? "CAD"} /></td>
                    <td style={{ color: "#B42318" }}>(-{discount?.loyalty_points_used})</td>
                  </tr>
                );
              })}
              <tr className="order-complete-table-totals">
                <td colSpan={2}>{t('Total')}</td>
                <td style={{ color: "#B42318" }}>-<LocaleCurrency amount={loyaltyPointsDiscounts?.reduce((total, discount) => total + discount?.amount, 0)} currency={facilityStore?.facility?.default_currency_code} /></td>
                <td style={{ color: "#B42318" }}>(-{loyaltyPointsDiscounts?.reduce((total, discount) => total + discount?.loyalty_points_used, 0)})</td>
              </tr>
            </DataTable>
          </div>
        ) : null}

        {/* EARNING POINTS */}
        {loyaltyCredit?.length > 0 ? (
          <div className="order-complete-table">
            <br />
            <DataTable
              columns={[{ label: t("") }, { label: t("") }, { label: t("Price") }, {
                label: t("Pts"), content: <div className="order-complete-table-header">{t("Pts")} <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id={"points-details"}>{t("Points earned or redeemed")} (+/-)</Tooltip>}
                >
                  <FontAwesomeIcon icon={faCircleQuestion} />
                </OverlayTrigger></div>,
              }]}
            >
              <tr className="order-complete-table-totals">
                <td colSpan={2}>{t('Total')}</td>
                <td><LocaleCurrency amount={order?.subtotal_price} currency={facilityStore?.facility?.default_currency_code} /></td>
                <td>+{loyaltyCredit?.reduce((total, transaction) => total += transaction?.amount, 0)}</td>
              </tr>
            </DataTable>
          </div>
        ) : null}

        <div className="mt-4">

          <FormLayout>
            <FormLayout.Group>
              <Button block size="medium" onClick={printReceipt} disabled={disableDuringLoad && !transactionComplete}>
                {t("Print Receipt")}
              </Button>
              <Button
                block
                size="medium"
                onClick={() =>
                  setEmailState(prevState => ({
                    ...prevState,
                    modalVisible: true,
                    email: order?.customer?.email ? order?.customer?.email : "",
                  }))
                }
                disabled={disableDuringLoad && !transactionComplete}
              >
                {t("Email Receipt")}
              </Button>
            </FormLayout.Group>
            {tee_time_id && (
              <FormLayout.Group>
                <Button block size="medium" onClick={() => void printCartWaiver()}>
                  {t("Print Cart Waiver")}
                </Button>
              </FormLayout.Group>
            )}
            {giftCardsLoading ? (
              <FormLayout.Group>
                <div className="h-8">
                  <Spin className="self-center" />
                </div>
                <Button block size="medium" onClick={closeActionBar} disabled={disableDuringLoad && loading}>
                  {t("No Receipt")}
                </Button>
              </FormLayout.Group>
            ) : orderGiftCards.length > 0 ? (
              <FormLayout.Group>
                <Button block size="medium" onClick={printGiftCardReceipts}>
                  {t("Print Gift Cards")}
                </Button>
                <Button block size="medium" onClick={closeActionBar} disabled={disableDuringLoad && loading}>
                  {t("No Receipt")}
                </Button>
              </FormLayout.Group>
            ) : (
              <FormLayout.Group>
                <Button block size="medium" onClick={closeActionBar} disabled={disableDuringLoad && loading}>
                  {t("No Receipt")}
                </Button>
              </FormLayout.Group>
            )}
            {order?.line_items?.some((item: Record<string, any>) => item?.product_type === "Ticket") && (
              <FormLayout.Group>
                <NavigationDropdownNew
                  label={t("Download Tickets")}
                  type="primary"
                  rightAlign
                  block
                  sections={[
                    [
                      {
                        label: t("Standard Format"),
                        onClick: () => void handleTicketDownload(),
                      },
                      {
                        label: t("Zebra Format"),
                        onClick: () => void handleTicketDownload("zebra"),
                      },
                    ],
                  ]}
                />
              </FormLayout.Group>
            )}
            {order?.line_items?.some((item: Record<string, any>) => item?.product_type === "e-Range") && (
              <FormLayout.Group>
                <Button block size="medium" onClick={printERangePins}>
                  {t("Print e-Range Pins")}
                </Button>
              </FormLayout.Group>
            )}
            {returnable && (
              <FormLayout.Group>
                <Button block size="medium" onClick={returnToTeeSheet} disabled={loading}>
                  {returnLabel ?? t("Return to Tee Sheet")}
                </Button>
              </FormLayout.Group>
            )}
          </FormLayout>

        </div>
        {/* )} */}
      </Popup>

      <Sheet
        title={t("Enter Email")}
        open={emailState.modalVisible}
        size="small"
        stacked
        closable
        onCancel={() => setEmailState(prevState => ({ ...prevState, modalVisible: false, email: "" }))}
        onOk={emailReceipt}
        okText={t("Send Email")}
        okDisabled={!emailState.email ? true : false}
      >
        <Input
          placeholder={t("Email")}
          value={emailState.email}
          id="email"
          onChange={handleEmailInputChange}
        />
      </Sheet>
    </>
  );
}
